import { useVerifyPostcode } from './useVerifyPostcode'
import type { DeliveryDate } from '@/models/DeliveryDate'
import { type DeliverySlot, DeliverySlotName } from '@/models/DeliverySlot'
import { useLykaApi } from '@/services/lyka-api'

const PRIORITIZED_DELIVERY_SLOT = DeliverySlotName.PM

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useDeliveryDates = () => {
  const api = useLykaApi()
  const verifyPostcode = useVerifyPostcode()

  const getSlots = (dates: DeliveryDate[]): DeliverySlot[] => {
    return dates.flatMap((d) => d.slots)
  }

  const getAvailableDeliveryDates = (dates: DeliveryDate[]): DeliveryDate[] => {
    return dates.filter(({ available }) => available)
  }

  const getDefaultDeliveryDate = (availableDeliveryDates: DeliveryDate[]): DeliveryDate | undefined => {
    const [first] = availableDeliveryDates

    return first
  }

  const getDefaultDeliverySlot = (defaultDeliveryDate: DeliveryDate): DeliverySlot | undefined => {
    const slots = [...(defaultDeliveryDate.slots ?? [])]

    return slots
      .sort((slot) => {
        // Prioritize PM slots to avoid AM deliveries as customers prefer not to be woken up early.
        if (slot.name === PRIORITIZED_DELIVERY_SLOT) {
          return -1
        }

        return 1
      })
      .find(({ available }) => available)
  }

  const getSlotsForDate = (date: string, availableDeliveryDates: DeliveryDate[]): DeliverySlot[] => {
    return (
      availableDeliveryDates
        .find((deliveryDate) => deliveryDate.date === date)
        ?.slots.filter(({ available }) => available) ?? []
    )
  }

  const loadDeliveryDates = async (postcode: string, weeks = 3): Promise<DeliveryDate[] | undefined> => {
    // First verify that the postcode is valid. We need to do this as a postcode
    // such as 2004 which is used for post office boxes will return valid delivery
    // slots. We don't delivery to PO Boxes so need to return no results.
    const valid = await verifyPostcode.verify(postcode)

    if (!valid) {
      return
    }

    const [ymd] = new Date().toISOString().split('T')

    const params = {
      postcode,
      weeks,
      ignoreChargeCutoff: 1,
      baseDate: ymd,
    }

    const { data } = await api.post<{ data: DeliveryDate[] }>('/api/v1/shipping/deliverydates/postcodeonly', params)

    if (Array.isArray(data)) {
      return data
    }
  }

  const getTimeForSlot = (slot: Pick<DeliverySlot, 'name'>): string | undefined => {
    switch (slot.name) {
      case DeliverySlotName.AM:
        return '12am - 7am'
      case DeliverySlotName.PM:
        return '8am - 6pm'
      default:
        return undefined
    }
  }

  return {
    getSlots,
    getDefaultDeliveryDate,
    getDefaultDeliverySlot,
    getAvailableDeliveryDates,
    getTimeForSlot,
    getSlotsForDate,
    loadDeliveryDates,
  }
}
